import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { QrCode, Copy, Eye, Users, Trophy, Wallet, X, ArrowsClockwise, Envelope } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';

const cents = (c) => `€${((c || 0) / 100).toFixed(2)}`;

// Robust JSON reader (avoids fetch-body-already-used conflicts with Sentry breadcrumbs).
const safeReadJson = async (res) => {
  try {
    // Clone before reading: Sentry fetch instrumentation may drain the original stream.
    const text = await res.clone().text();
    if (!text) return {};
    try { return JSON.parse(text); } catch { return { _raw: text }; }
  } catch { return {}; }
};

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="bg-[#0e0b04] border border-[#cba135]/15 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="uppercase text-[10px] tracking-[0.22em] text-[#a8872b] font-semibold">{label}</span>
        {Icon && <Icon size={16} weight="duotone" className="text-[#cba135]/60" />}
      </div>
      <p className="heading-font text-2xl text-[#cba135] mt-1 tabular-nums">{value}</p>
      {sub && <p className="text-[10px] text-[#cba135]/50 mt-1">{sub}</p>}
    </div>
  );
}

const statusBadge = (s) => {
  const map = {
    paid: 'bg-emerald-500/10 text-emerald-300',
    approved: 'bg-blue-500/10 text-blue-300',
    pending: 'bg-amber-500/10 text-amber-300',
    rejected: 'bg-red-500/10 text-red-300',
    no_purchase: 'bg-[#cba135]/10 text-[#cba135]/60',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${map[s] || 'bg-[#cba135]/10 text-[#cba135]/60'}`}>
      {s === 'no_purchase' ? 'no purchase' : s}
    </span>
  );
};

export default function AffiliateDetailDialog({ open, onOpenChange, code, token, apiBase, autoRefreshMs = 15000 }) {
  const auth = { Authorization: `Bearer ${token}` };
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    if (!code) return;
    try {
      const [sRes, aRes, fRes] = await Promise.all([
        fetch(`${apiBase}/admin/affiliates/${code}/stats`, { headers: auth }),
        fetch(`${apiBase}/admin/affiliates/${code}/activity`, { headers: auth }),
        fetch(`${apiBase}/admin/affiliates/${code}/funnel`, { headers: auth }),
      ]);
      const s = await safeReadJson(sRes);
      const a = await safeReadJson(aRes);
      const f = await safeReadJson(fRes);
      if (s?.affiliate) setStats(s);
      if (a?.items) setActivity(a.items);
      if (f?.funnel) setFunnel(f);
      setLastUpdated(new Date());
    } catch (e) {
      // Non-fatal: dialog is auto-refreshing, next poll will retry.
      // Log to console for ops visibility without blocking the UI.
      if (typeof console !== 'undefined') console.debug('[AffiliateDetailDialog] stats/activity fetch failed:', e);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, apiBase, token]);

  // Initial load + reset on open
  useEffect(() => {
    if (open && code) {
      setLoading(true);
      setStats(null);
      setActivity([]);
      setFunnel(null);
      load();
    }
  }, [open, code, load]);

  // Real-time poll while dialog is open
  useEffect(() => {
    if (!open || !autoRefreshMs) return undefined;
    const id = setInterval(load, autoRefreshMs);
    return () => clearInterval(id);
  }, [open, autoRefreshMs, load]);

  const aff = stats?.affiliate;
  const conversionPct = stats?.clicks > 0 ? Math.round((stats.registrations / stats.clicks) * 1000) / 10 : 0;
  const paidCount = stats?.rewardsByStatus?.paid?.count || 0;

  const copyLink = async () => {
    if (!stats?.referralUrl) return;
    try {
      await navigator.clipboard.writeText(stats.referralUrl);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const copyEmail = async () => {
    if (!aff?.email) return;
    try {
      await navigator.clipboard.writeText(aff.email);
      toast.success('Email copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1508] border border-[#cba135]/20 text-[#cba135] max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="affiliate-detail-dialog">
        {loading ? (
          <div className="py-12 text-center text-[#cba135]/60">Loading…</div>
        ) : !aff ? (
          <div className="py-12 text-center text-[#cba135]/60">Affiliate not found.</div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-[#cba135]/10 pb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#a8872b]">Partner view (live)</p>
                <h2 className="heading-font text-3xl text-[#cba135] mt-1" data-testid="detail-partner-code">{aff.code}</h2>
                <p className="text-sm text-[#cba135]/70 mt-0.5">
                  {aff.name || 'Partner'} · {(() => {
                    const ticketC = aff.cpaTicketCents ?? aff.cpaAmountCents ?? 0;
                    const parts = [];
                    if (ticketC > 0) parts.push(`€${(ticketC / 100).toFixed(2)}/ticket`);
                    if (aff.cpaRegistrationCents > 0) parts.push(`€${(aff.cpaRegistrationCents / 100).toFixed(2)}/reg`);
                    if (aff.cpcCents > 0) parts.push(`€${(aff.cpcCents / 100).toFixed(2)}/click`);
                    if (aff.revsharePercent > 0) parts.push(`${aff.revsharePercent}% rev`);
                    return parts.join(' + ') || 'no reward';
                  })()} · status: {aff.status}
                </p>
                {/* Partner contact email — prominently shown, click to copy, opens mail client */}
                <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="detail-partner-email-row">
                  <Envelope size={14} weight="duotone" className="text-[#cba135]/70" />
                  {aff.email ? (
                    <>
                      <a
                        href={`mailto:${aff.email}`}
                        className="text-sm font-mono text-[#e6be4b] hover:text-[#cba135] underline-offset-2 hover:underline"
                        data-testid="detail-partner-email"
                      >
                        {aff.email}
                      </a>
                      <button
                        onClick={copyEmail}
                        className="text-[10px] uppercase tracking-wider text-[#cba135]/60 hover:text-[#cba135] px-1.5 py-0.5 rounded border border-[#cba135]/20 hover:border-[#cba135]/50 inline-flex items-center gap-1"
                        data-testid="detail-partner-email-copy"
                        title="Copy email to clipboard"
                      >
                        <Copy size={10} /> Copy
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-[#cba135]/40 italic" data-testid="detail-partner-email-empty">
                      No email on file
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#cba135]/50">
                <ArrowsClockwise size={14} className="animate-spin-slow" />
                Auto-refresh {Math.round(autoRefreshMs / 1000)}s
                {lastUpdated && <span className="hidden sm:inline">· {lastUpdated.toLocaleTimeString()}</span>}
                <button
                  onClick={() => onOpenChange(false)}
                  className="ml-2 p-1.5 rounded hover:bg-[#cba135]/10 text-[#cba135]/70 hover:text-[#cba135]"
                  data-testid="affiliate-detail-close"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Stats grid (mirrors PartnerDashboard) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <StatCard testid="detail-stat-clicks" label="Clicks" value={stats.clicks} icon={Eye} />
              <StatCard testid="detail-stat-regs" label="Registrations" value={stats.registrations} icon={Users} />
              <StatCard testid="detail-stat-cr" label="Conversion" value={`${conversionPct}%`} sub="regs / clicks" />
              <StatCard testid="detail-stat-paid" label="Paid rewards" value={paidCount} icon={Trophy} />
              <StatCard
                testid="detail-stat-earned"
                label="Earned"
                value={cents(stats.paidCents)}
                sub={`Pending: ${cents((stats.earningsCents || 0) - (stats.paidCents || 0))}`}
                icon={Wallet}
              />
            </div>

            {/* QR + Link */}
            <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4 bg-[#0e0b04] border border-[#cba135]/15 rounded-lg p-4">
              <div className="flex flex-col items-center">
                <div className="bg-white p-2 rounded">
                  <img src={stats.qrUrl} alt={`QR ${aff.code}`} className="w-32 h-32 block" data-testid="detail-qr-img" />
                </div>
                <a
                  href={stats.qrUrl}
                  download={`affiliate-${aff.code}.png`}
                  className="mt-2 text-[11px] uppercase tracking-wider text-[#cba135]/70 hover:text-[#e6be4b] inline-flex items-center gap-1"
                  data-testid="detail-qr-download"
                >
                  <QrCode size={12} /> Download PNG
                </a>
              </div>
              <div className="flex flex-col justify-center min-w-0">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#a8872b] mb-1">Referral link</p>
                <div className="bg-[#1a1508] border border-[#cba135]/20 rounded px-2.5 py-2 font-mono text-xs text-[#e6be4b] break-all" data-testid="detail-referral-link">
                  {stats.referralUrl}
                </div>
                <Button onClick={copyLink} size="sm" className="mt-2 self-start bg-[#cba135] hover:bg-[#b8902f] text-[#0e0b04] font-medium" data-testid="detail-copy-link">
                  <Copy size={12} /> Copy link
                </Button>
              </div>
            </div>

            {/* Conversion funnel + fraud signals */}
            {funnel?.funnel && (
              <div data-testid="detail-funnel-block">
                <h3 className="heading-font text-base text-[#cba135] mb-2">Funnel &amp; fraud signals</h3>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="bg-[#0e0b04] border border-[#cba135]/15 rounded-lg p-3" data-testid="detail-funnel-clicks">
                    <p className="text-[10px] uppercase tracking-wider text-[#cba135]/60">Clicks</p>
                    <p className="text-xl font-mono text-[#cba135] mt-1">{funnel.funnel.clicks}</p>
                  </div>
                  <div className="bg-[#0e0b04] border border-[#cba135]/15 rounded-lg p-3" data-testid="detail-funnel-regs">
                    <p className="text-[10px] uppercase tracking-wider text-[#cba135]/60">Registrations</p>
                    <p className="text-xl font-mono text-[#cba135] mt-1">{funnel.funnel.registrations}</p>
                    <p className="text-[10px] text-[#cba135]/50 mt-0.5">{funnel.funnel.clickToRegPct}% from clicks</p>
                  </div>
                  <div className="bg-[#0e0b04] border border-[#cba135]/15 rounded-lg p-3" data-testid="detail-funnel-tickets">
                    <p className="text-[10px] uppercase tracking-wider text-[#cba135]/60">Ticket buyers</p>
                    <p className="text-xl font-mono text-[#cba135] mt-1">{funnel.funnel.ticketBuyers}</p>
                    <p className="text-[10px] text-[#cba135]/50 mt-0.5">{funnel.funnel.regToTicketPct}% from regs</p>
                  </div>
                </div>
                {funnel.fraudSignals?.suspiciousClusterIps > 0 ? (
                  <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3" data-testid="detail-fraud-alert">
                    <p className="text-[11px] uppercase tracking-wider text-red-400 font-bold">⚠ Fraud signal</p>
                    <p className="text-xs text-red-300 mt-1">
                      {funnel.fraudSignals.suspiciousClusterIps} IP cluster{funnel.fraudSignals.suspiciousClusterIps > 1 ? 's' : ''}
                      &nbsp;detected — {funnel.fraudSignals.usersInClusters} registered users share fewer than {funnel.fraudSignals.suspiciousClusterIps} distinct IPs.
                      Affected rewards are auto-held for manual review.
                    </p>
                  </div>
                ) : (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2.5" data-testid="detail-fraud-clean">
                    <p className="text-[11px] text-emerald-400/80">✓ No suspicious IP clusters detected.</p>
                  </div>
                )}
              </div>
            )}

            {/* Recent activity */}
            <div>
              <h3 className="heading-font text-base text-[#cba135] mb-2">Recent activity</h3>
              <div className="bg-[#0e0b04] border border-[#cba135]/15 rounded-lg p-4">
                {activity.length === 0 ? (
                  <p className="text-[#cba135]/55 text-xs" data-testid="detail-activity-empty">
                    No referrals yet. Once people register via the link, you'll see them here.
                  </p>
                ) : (
                  <div className="overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="text-[#a8872b] uppercase text-[10px] tracking-wider sticky top-0 bg-[#0e0b04]">
                        <tr className="border-b border-[#cba135]/10">
                          <th className="text-left py-2 px-2">User</th>
                          <th className="text-left py-2 px-2">Country</th>
                          <th className="text-left py-2 px-2">Joined</th>
                          <th className="text-center py-2 px-2">Status</th>
                          <th className="text-right py-2 px-2">Reward</th>
                        </tr>
                      </thead>
                      <tbody className="text-[#cba135]/85">
                        {activity.map((r, i) => {
                          // Stable composed key: joinedAt+maskedName is unique across activity rows
                          const rowKey = `${r.joinedAt || ''}-${r.maskedName || ''}-${i}`;
                          return (
                          <tr key={rowKey} className="border-b border-[#cba135]/5" data-testid={`detail-activity-row-${i}`}>
                            <td className="py-2 px-2 font-mono">{r.maskedName}</td>
                            <td className="py-2 px-2">{r.country || '—'}</td>
                            <td className="py-2 px-2 text-[#cba135]/60">{timeAgo(r.joinedAt)}</td>
                            <td className="py-2 px-2 text-center">{statusBadge(r.rewardStatus)}</td>
                            <td className="py-2 px-2 text-right">{r.rewardAmountCents ? cents(r.rewardAmountCents) : '—'}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <p className="text-[10px] text-[#cba135]/40 mt-2 uppercase tracking-wider">
                Same view the partner sees in their dashboard. Names masked for privacy.
              </p>
            </div>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
